import { LoginProps, UserApiRequest } from "@models/components/auth.model";
import { UsePaymentMethodParams } from "@models/components/paymentMethod.model";
import { updateProfile } from "@models/components/user.model";
import { resolveUrl } from "@utils/apiUrlResolver.utils";

import API_URL from "./apiConfig/api.config";
import apiCall from "./apiConfig/apiCall.config";

export async function loginUser(email: string, password: string) {
  return await apiCall.post(API_URL.AUTH.LOGIN_USER, { email, password });
}

export async function notificationSubscribe(fcmId: string) {
  return await apiCall.post(API_URL.NOTIFICATION.SUBSCRIBE, {
    fcmId,
    userAgent: navigator.userAgent,
  });
}

export async function notificationUnsubscribe(fcmId: string) {
  return await apiCall.post(API_URL.NOTIFICATION.SUBSCRIBE, { fcmId });
}

export async function notificationEnable(fcmId: string) {
  return await apiCall.post(API_URL.NOTIFICATION.ENABLE, {
    fcmId,
    userAgent: navigator.userAgent,
  });
}

export async function notificationDisable(fcmId: string) {
  return await apiCall.post(API_URL.NOTIFICATION.DISABLE, { fcmId });
}

export async function createUserDetails(data: UserApiRequest) {
  return await apiCall.post(API_URL.USER.REGISTER, data);
}

export async function getMemberInfo() {
  return await apiCall.get(API_URL.USER.MEMBER_INFO);
}

export async function getOrganisationList(refresh: boolean = false) {
  return await apiCall.get(
    API_URL.DASHBOARD.ORGANIZATION_FINDER + (refresh ? "?refresh=true" : "")
  );
}

export async function getOrganizationsMetaData(
  userLat?: number,
  userLong?: number
) {
  const params: Record<string, number> = {};
  if (userLat !== undefined) {
    params.userLat = userLat;
  }
  if (userLong !== undefined) {
    params.userLong = userLong;
  }

  return await apiCall.get(
    API_URL.DASHBOARD.META_DATA,
    undefined,
    Object.keys(params).length > 0 ? params : undefined
  );
}

export async function getRegisterOptions() {
  return await apiCall.get(API_URL.USER.REGISTER_OPTIONS);
}

export async function getUserBookings() {
  return await apiCall.get(API_URL.USER.USER_BOOKINGS);
}

export async function getUserBookingDetails(bookingId: string) {
  return await apiCall.get(
    resolveUrl(
      API_URL.USER.USER_BOOKING_DETAILS,
      API_URL.KIOSK.USER_BOOKING_DETAILS
    ) + bookingId
  );
}
export async function getUserUpdatePaymentStatus(bookingId: string) {
  return await apiCall.patch(
    resolveUrl(
      API_URL.USER.USER_UPDATE_PAYMENT_STATUS,
      API_URL.KIOSK.USER_UPDATE_PAYMENT_STATUS
    ) + bookingId
  );
}

export async function getUserProfile() {
  return await apiCall.get(
    resolveUrl(API_URL.USER.USER_PROFILE, API_URL.KIOSK.USER_PROFILE)
  );
}

export async function getUserSelectedLanguage() {
  return await apiCall.get(
    resolveUrl(
      API_URL.USER.GET_SELECTED_LANGUAGE,
      API_URL.KIOSK.GET_SELECTED_LANGUAGE
    )
  );
}

export async function login({ email, sessionCode }: LoginProps) {
  return await apiCall.post(API_URL.AUTH.LOGIN, { email, sessionCode });
}

export async function loginAccess(email: string) {
  return await apiCall.post(API_URL.AUTH.LOGIN_ACCESS, { email });
}

export async function logOut() {
  await apiCall.api.deleteHeader("Authorization");
}

export async function mailVerification(
  email: string,
  verificationCode: string
) {
  return await apiCall.get(
    `${API_URL.AUTH.MAIL_VERIFICATION}${email}/${verificationCode}`
  );
}

export async function resendMailVerification(email: string) {
  return await apiCall.get(`${API_URL.AUTH.RESEND_MAIL_VERIFICATION}${email}`);
}

export async function updateUserLanguage(language: string) {
  return await apiCall.patch(
    resolveUrl(
      API_URL.USER.GET_SELECTED_LANGUAGE,
      API_URL.KIOSK.GET_SELECTED_LANGUAGE
    ),
    { language }
  );
}

export async function updateUserProfile(data: updateProfile) {
  return await apiCall.put(API_URL.USER.USER_PROFILE, { ...data });
}

export async function resetPassword(email: string) {
  return await apiCall.post(API_URL.PASSWORD.RESET_PASSWORD, {
    email,
  });
}
export async function getPasswordTokenValid(token: string) {
  return await apiCall.get(`${API_URL.PASSWORD.RESET_PASSWORD_TOKEN}/${token}`);
}
export async function setPassword(password: string, token: string) {
  return await apiCall.post(`${API_URL.PASSWORD.SET_PASSWORD}/${token}`, {
    password,
  });
}
export async function checkAccountDeletion() {
  return await apiCall.get(API_URL.DELETEACCOUNT.CHECK_ACCOUNT_DELETE);
}
export async function accountDeletion(password: string) {
  return await apiCall.post(API_URL.DELETEACCOUNT.ACCOUNT_DELETE, {
    password,
  });
}
export async function getRegistrationInvitation(profileId: string) {
  return await apiCall.get(`${API_URL.AUTH.INVITATION_PROFILE}/${profileId}`);
}
export async function invitedSetPassword(password: string, token: string) {
  return await apiCall.post(
    `${API_URL.PASSWORD.INVITED_SET_PASSWORD}/${token}`,
    {
      password,
    }
  );
}
export async function checkInvitationPageShow(token: string) {
  return await apiCall.get(`${API_URL.AUTH.INVITATION_VALID}/${token}`);
}

export async function getKioskSetupData() {
  return await apiCall.get(
    `${API_URL.USER.KIOSK_SETUP_DATA}`,
    undefined,
    undefined,
    "v2"
  );
}
export async function setHouseholdShareMyPin() {
  return await apiCall.post(
    `${API_URL.USER.KIOSK_SHARE_MY_PIN}`,
    undefined,
    undefined,
    "v2"
  );
}

export async function setHouseholdIndividualPin() {
  return await apiCall.post(
    `${API_URL.USER.KIOSK_INDIVIDUAL_PIN}`,
    undefined,
    undefined,
    "v2"
  );
}
export async function setProfilePin(payload: { pin: string }) {
  return await apiCall.post(
    `${API_URL.USER.KIOSK_PROFILE_PIN}`,
    payload,
    undefined,
    "v2"
  );
}
export async function postHouseholdIndividualPin(
  profileId: number,
  payload: { pin: string }
) {
  return await apiCall.post(
    `${API_URL.USER.KIOSK_HOUSEHOLD_PROFILE_PIN}/${profileId}`,
    payload,
    undefined,
    "v2"
  );
}
export async function deleteTempHouseholdMember(profileId: number) {
  return await apiCall.post(
    `${API_URL.USER.REMOVE_TEMP_HOUSEHOLD_MEMBER}/${profileId}`,
    undefined,
    undefined,
    "v2"
  );
}

export async function getPublicBookingDetails(bookingId: string) {
  return await apiCall.get(`${API_URL.BOOKING.BOOKING_SHARE}/${bookingId}`);
}

export async function getMemberClubs() {
  return await apiCall.get(API_URL.USER.MEMBER_CLUBS);
}

export async function getUserMemberships() {
  return await apiCall.get(
    API_URL.USER.MEMBERSHIPS,
    undefined,
    undefined,
    "v2"
  );
}

export async function getTokenisationPaymentMethods() {
  return await apiCall.get(
    API_URL.TOKENISATION.PAYMENT_METHODS,
    undefined,
    undefined,
    "v2"
  );
}

export async function makeDefaultPaymentMethod(paymentId: string | number) {
  return await apiCall.patch(
    `${API_URL.TOKENISATION.PAYMENT_METHODS}/${paymentId}/default`,
    {},
    undefined,
    "v2"
  );
}

export async function deletePaymentMethod(paymentId: string | number) {
  return await apiCall.remove(
    `${API_URL.TOKENISATION.PAYMENT_METHODS}/${paymentId}`,
    undefined,
    "v2"
  );
}
export async function getTokenisationSaveCards(
  payload?: UsePaymentMethodParams
) {
  return await apiCall.post(API_URL.USER.SAVED_CARDS, payload, undefined, "v2");
}
